// API Response Types
export interface ApiResponse<T = unknown> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

export interface PaginatedResponse<T = unknown> {
  success: boolean;
  data: T[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  page?: number;
  pageSize?: number;
  total?: number;
  totalPages?: number;
}

// Association Types
export interface Association {
  Id: number;
  Name: string;
  LogoId: number | null;
  /** Attachment ID for favicon (served from CDN similarly to LogoId) */
  FavIconId?: number | null;
  /** New API: direct URL for the logo (preferred over LogoId) */
  LogoUrl?: string | null;
  /** New API: direct URL for the favicon (preferred over FavIconId) */
  FavIconUrl?: string | null;
  PortalHostName: string | null;
  Email: string | null;
  Mobile: string | null;
  Address: string | null;
  ShortDescription: string | null;
  Facebook: string | null;
  Twitter: string | null;
  Instagram: string | null;
  Youtube: string | null;
  Snapchat: string | null;
  WhatsApp: string | null;
  TikTok: string | null;
  IsActive: boolean;
  // NOTE: We intentionally do not depend on AdminThemeClass for theming.
  // The UI theme is driven only by CustomThemeColor1/2/3 (ASP.NET ThemeX parity).
  AdminThemeClass?: string | null;
  /**
   * ASP.NET ThemeX support:
   * - color1: main color (top bar, footer, main buttons)
   * - color2: secondary color (links, secondary buttons)
   * - color3: third color (some secondary buttons)
   */
  CustomThemeColor1?: string | null;
  CustomThemeColor2?: string | null;
  CustomThemeColor3?: string | null;
  ActiveBankTransferMethod?: boolean | null;
  ActiveVisaCardMethod?: boolean | null;
  BankTransferNotification?: boolean | null;
  /** Optional: custom footer text; registration cert shown in footer when set */
  FooterText?: string | null;
  LicenseImageUrl?: string | null;
  LicenseUrl?: string | null;
  /** Server-persisted theme editor JSON blob */
  PortalThemeConfig?: string | null;
}

/**
 * Association Social Media Config (GET /associations/{id}/social-media-config)
 */
export interface AssociationSocialLinks {
  facebook?: string | null;
  twitter?: string | null;
  youtube?: string | null;
  instagram?: string | null;
  snapchat?: string | null;
  whatsapp?: string | null; // phone number or URL depending on backend
  tiktok?: string | null;
  [key: string]: unknown;
}

export interface AssociationSocialMediaConfigAnalytics {
  googleAnalyticsId?: string | null;
  googleTagManagerHead?: string | null;
  googleTagManagerBody?: string | null;
  /**
   * May be either a raw pixel ID (e.g. "123...") OR a full HTML snippet containing <script>/<noscript>.
   * Tracking layer handles both.
   */
  facebookPixelId?: string | null;
  twitterWebsiteTag?: string | null;
  snapchatPixelId?: string | null;
  snapchatCapiToken?: string | null;
  tiktokPixelId?: string | null;
  metaCapiToken?: string | null;
  hotjarScriptHead?: string | null;
  webEngageSDK?: string | null;
  clarityProjectId?: string | null;
  [key: string]: unknown;
}

export interface AssociationSocialMediaConfigFeatures {
  whatsappIconEnabled?: boolean;
  whatsappScript?: string | null;
  [key: string]: unknown;
}

export interface AssociationSocialMediaConfigData {
  socialLinks?: AssociationSocialLinks;
  analytics?: AssociationSocialMediaConfigAnalytics;
  features?: AssociationSocialMediaConfigFeatures;
  /**
   * Some environments bucket legacy “script blob” values under `scripts`.
   * Tracking/widgets layers do best-effort extraction from this object.
   */
  scripts?: Record<string, unknown>;
  [key: string]: unknown;
}

// Project Types
export interface Project {
  Id: number;
  Name: string;
  /** Raw multilingual JSON for `Name` preserved through `transformProject` so consumers (e.g. cart) can re-resolve on locale change. */
  NameRaw?: string | null;
  NameAdmin?: string | null;
  AssociationId: number;
  Details: string | null;
  ShortDescription: string | null;
  RequiredAmount: number | null;
  PaidAmount: number | null;
  NoDoners: number | null;
  LogoId: string | null;
  /** Raw multilingual JSON for `LogoId` preserved through `transformProject` so consumers (e.g. cart) can re-resolve on locale change. */
  LogoIdRaw?: string | null;
  /** New API: direct URL for the logo (preferred over LogoId) */
  LogoUrl?: string | null;
  IsActive: boolean;
  ShowInHomePage: boolean | null;
  GlobalGift?: boolean | null;
  AdvancedGift?: boolean | null;
  IsProduct: boolean | null;
  GiftProject: boolean | null;
  PageUrlKey?: string | null;
  CategoryIds: string | null;
  MinimumAmount: number | null;
  OrderNumber: number | null;
  AssociationName?: string;
  CategoryName?: string;
  OtherImageIds: string | null;
  DonateTypeId: number | null;
  ExternalAmounts: number | null;
  /** Canonical raised total from ERP: paid + archive + external. */
  RaisedAmount?: number | null;
  DisplayIndicatorAchievement: boolean | null;
  ShowPercentageAchievementIndicator: boolean | null;
  EndowmentCertificate: boolean | null;
  AllowEnterText?: boolean | null;
  EnterTextLabel?: string | null;
  EnterTextPlaceholder?: string | null;
  IsBriefProject: boolean | null;
  EndowmentCertificateImageId?: string | null;
  RelatedGiftProjectId: number | null;
  QuantityStock: number | null;
  /** Association's stored custom QR URL; when absent the UI falls back to the canonical project URL. */
  QrCodeUrl?: string | null;
  /** Pre-loaded shares from the homepage bundle (undefined = not provided, [] = no shares). */
  shares?: ProjectShare[];
  // Calculated fields
  NewPaidAmount?: number;
  NewRequiredAmount?: number;
  ProgressRatio?: number;
  IsProjectCompleted?: boolean;
  /** Server-authoritative: whether new donations may be accepted. */
  CanDonate?: boolean;
}

export interface ProjectDynamicItem {
  projectId: number;
  associationId: number;
  raisedAmount: number;
  remainingAmount: number;
  progressPercent: number;
  isCompleted: boolean;
  canDonate: boolean;
  isActive: boolean;
  updatedAt: string;
}

export interface ProjectDynamicBatchResponse {
  success: boolean;
  data?: Record<string, ProjectDynamicItem>;
  meta?: {
    associationId: number;
    count: number;
    requestedIds: number[];
    cache: {
      hits: number;
      misses: number;
    };
  };
  error?: string;
  message?: string;
}

// Category Types
export interface Category {
  Id: number;
  Name: string;
  Description?: string | null;
  ImageId: number | null;
  /** New API: direct URL for the category image */
  ImageUrl?: string | null;
  BannerImageId?: number | null;
  BannerImageUrl?: string | null;
  ImageCampaignId?: number | null;
  ImageCampaignUrl?: string | null;
  OrderNumber: number;
  IsActive: boolean;
  AssociationId: number | null;
  ViewHomePage: boolean | null;
  ViewQuickDonation?: boolean | null;
  ViewInCampaigns?: boolean | null;
  ViewInDeductions?: boolean | null;
  HideCategoryProject?: boolean | null;
  ExternalLink?: string | null;
  PaidAmount: number | null;
  NoDoners: number | null;
}

// Cart Types
export interface CartItem {
  Id: number;
  ProjectId: number;
  Amount: number;
  Quantity: number;
  ProjectShareId: number | null;
  // ASP.NET-like fields used for identity/merge & rules
  DonateTypeId?: number | null;
  CategoryId?: number | null;
  DonationOpen?: boolean;
  IsProduct?: boolean | null;
  GiftProject?: boolean | null;
  ProjectName?: string;
  /** Label for category (general/quick) lines, which carry no project. */
  categoryName?: string;
  ProjectImage?: string | null;
  giftInfo?: GiftInfo;
  isRecurring?: boolean;
  recurringDate?: Date;
  certificateInfo?: CertificateInfo;
  enterText?: string;
  /** Recurring deduction metadata (may be absent on authenticated DB carts). */
  periodType?: number;
  totalPeriod?: number;
  openEnded?: boolean;
  deductionProject?: boolean;
  dayNumber?: number;
}

// Donation Types
export interface DonationOperation {
  Id: number;
  AssociationId: number;
  TotalAmount: number;
  OperationCode: string;
  PaymentMethodId: number;
  StatusId: number;
  DonationDate: Date;
  DonorUserId: string;
}

// Lookup Types
export interface PaymentMethod {
  Id: number;
  Name: string;
  IsActive: boolean;
  OrderNumber: number | null;
}

export interface City {
  Id: number;
  Name: string;
  IsActive: boolean;
}

export interface Bank {
  Id: number;
  Name: string;
  IsActive: boolean;
}

// Content Types
export interface BlogPost {
  Id: number;
  Title: string;
  Content: string;
  BlogTypeId: number | null;
  AssociationId: number;
  ImageId: number | null;
  /** New API: direct URL for the image */
  ImageUrl?: string | null;
  PublishDate: Date | null;
  Views: number | null;
  IsActive: boolean;
}

export interface NewsArticle {
  Id: number;
  Title: string;
  /**
   * Some environments return `Content` (legacy), others return `Description` / `ShortDescription`.
   * Keep these optional to support both response shapes.
   */
  Content?: string;
  Description?: string | null;
  ShortDescription?: string | null;
  NewsTypeId: number | null;
  AssociationId: number;
  ImageId?: number | null;
  MainImageId?: number | null;
  /** New API: direct URLs for images (preferred over ImageId/MainImageId) */
  ImageUrl?: string | null;
  MainImageUrl?: string | null;
  PublishDate?: Date | null;
  Views?: number | null;
  IsActive: boolean;
  // API sometimes returns `CreateDate` / `LastUpdate` as ISO strings
  CreateDate?: string | Date;
  LastUpdate?: string | Date;
  SeoId?: number | null;
  PageUrlKey?: string | null;
  CreationDate?: Date;
}

export interface FAQ {
  Id: number;
  Question: string;
  Answer: string;
  AssociationId: number;
}

/** Legacy PascalCase shape (kept for reference). Prefer ContactMessagePayload for API. */
export interface ContactMessage {
  ContactMessageTypeId: number;
  Title: string;
  Description: string;
  AssociationId: number;
  CreationUser: string;
  Mobile?: string;
  ImageId?: number;
  ImageUrl?: string | null;
}

/** Request body for POST /api/v1/contact/messages (camelCase). */
export interface ContactMessagePayload {
  contactMessageTypeId: number;
  title: string;
  description: string;
  creationUser: string;
  mobile?: string;
  imageId?: number | null;
}

export interface VolunteerApplication {
  name: string;
  mobile: string;
  email?: string;
  /** ISO date (YYYY-MM-DD); association is resolved server-side from the portal host. */
  dateBirth?: string;
  workAcademicLevel?: string;
  affiliate?: string;
  previousVolunteer?: boolean;
  otherInterests?: string;
}

/** Request body for POST /api/v1/project-requests (camelCase). */
export interface ProjectRequestPayload {
  name: string;
  mobile: string;
  city: string;
  projectScope: string;
  projectValue: string;
  durationProject: string;
}

// Statistics Types
export interface DashboardStats {
  active_projects: number;
  month_donations: number;
  today_donations: number;
  today_donations_count: number;
  total_collected: number;
  total_donations: number;
  total_donors: number;
  total_projects: number;
  total_target: number;
  total_users: number;
}

// Gift Types
export interface ProjectShare {
  Id: number;
  AssociationId: number;
  ProjectId: number;
  AttributeIndex: number | null;
  CreationDate: Date;
  CreationUser: string;
  LastModifiedDate: Date;
  LastModifiedUser: string;
  IsDeleted: boolean;
  ShareName: string; // Multi-lang JSON or plain text
  ShareAmount: number;
  Amount?: number; // Alias for ShareAmount
  RequiredQuantity: number;
  ShareUnitName: string | null;
  ImageId: string;
  /** New API: direct URL for the share image */
  ImageUrl?: string | null;
  PeriodType: number;
  OrderNumber: number;
  DonationOpen: boolean;
  ViewImageId: string;
  /** New API: direct URL for the share view image */
  ViewImageUrl?: string | null;
  GiftCategoryId?: number | null;
  MinDonationAmount?: number | null;
  UseAmountClasses?: boolean;
  AmountClasses?: GiftAmountClass[];
  EndPeriod: Date | null;
  DefaultDonation: boolean | null;
}

export interface GiftAmountClass {
  Id: number;
  Amount: number;
  OrderNumber?: number | null;
}

export interface AdvancedGiftCategory {
  Id: number;
  ProjectId: number;
  LookupCategoryId?: number | null;
  CategoryName: string;
  OrderNumber?: number | null;
  Shares: ProjectShare[];
}

export interface AdvancedGiftData {
  project: ApiProjectResponse;
  categories: AdvancedGiftCategory[];
}

export interface GiftAttribute {
  Id: number;
  ProjectId: number;
  AttributeName: string;
  AttributeValue: string | null;
  OrderNumber: number;
}

export interface GiftRecipient {
  Id?: number;
  DonationOperationDetailId?: number;
  SendToName: string;
  SendToMobile: string;
  SenderName: string;
  Message?: string;
  SendTime?: Date | null;
  SendNow?: boolean;
}

export interface RecurringDonation {
  Id?: number;
  UserId: string;
  ProjectId: number;
  Amount: number;
  AssociationId: number;
  BillsDate: Date;
  ProjectShareId?: number;
  Quantity?: number;
  IsPaid?: boolean;
}

export interface Partner {
  Id: number;
  Name: string;
  LogoId: string | null;
  /** New API: direct URL for the logo (preferred over LogoId) */
  LogoUrl?: string | null;
  Link?: string;
  AssociationId: number;
  OrderNumber: number;
  IsActive: boolean;
}

export interface HeroSlide {
  Id: number;
  Name?: string | null;
  Title?: string | null;
  SubTitle?: string | null;
  Details?: string | null;
  ImageId: number;
  ImageUrl?: string;
  ImageLink?: string | null;
  Action1Title?: string | null;
  Action1Link?: string | null;
  Action2Title?: string | null;
  Action2Link?: string | null;
  TitleColor?: string | null;
  SubTitleColor?: string | null;
  DetailsColor?: string | null;
  Action1TitleColor?: string | null;
  Action2TitleColor?: string | null;
  OrderNumber?: number;
  IsActive?: boolean;
}

export interface GiftInfo {
  recipientName?: string;
  recipientMobile?: string;
  senderName?: string;
  message?: string;
  sendTiming?: "now" | "later";
  sendTime?: string;
  sendSmsCopy?: boolean;
  attribute?: string;
}

export interface CertificateInfo {
  certificateName: string;
  sendCertificateSms: boolean;
}

// Testimonial Types
export interface Testimonial {
  Id: number;
  AssociationId: number;
  PersonName: string;
  Testimonial: string;
  CreateDate: Date;
  ImageId: number | null;
  /** New API: direct URL for the image */
  ImageUrl?: string | null;
}

// SMS Donation Types
export interface SmsDonation {
  Id: number;
  AssociationId: number;
  PhoneNumber: string;
  MessageBody: string;
  ImageId: number | null;
  ImageUrl?: string | null;
}

// Bank Account Types
export interface BankAccount {
  Id: number;
  BankName: string;
  AccountId: string;
  AccountIban?: string | null;
}

// Portal Page Types
export interface PortalPage {
  Id: number;
  PageTitle: string;
  PageContent: string;
  PageLink?: string;
  PageKeyTitle?: string; // Slug/identifier for the page
  PageSlug?: string; // Alternative slug field
  ImageId?: number | null;
  ImageUrl?: string | null;
  IsTopMenu?: boolean;
  IsFooterMenu?: boolean;
  PageOrder?: number;
}

export interface PortalMenuItem {
  Id: number;
  PageTitle: string;
  PageLink: string;
  IsTopMenu: boolean;
  IsFooterMenu: boolean;
  PageOrder: number;
}

// API Raw Response Types (before transformation)
export interface ApiProjectResponse {
  Id: number;
  Name: string;
  AssociationId: number;
  Details: string | null;
  ShortDescription: string | null;
  RequiredAmount: number | null;
  PaidAmount: number | null;
  NoDoners: number | null;
  LogoId: string | null;
  LogoUrl?: string | null;
  IsActive: boolean;
  ShowInHomePage: boolean | null;
  GlobalGift?: boolean | null;
  AdvancedGift?: boolean | null;
  IsProduct: boolean | null;
  GiftProject: boolean | null;
  PageUrlKey?: string | null;
  CategoryIds: string | null;
  MinimumAmount: number | null;
  OrderNumber: number | null;
  OtherImageIds: string | null;
  DonateTypeId: number | null;
  ExternalAmounts: number | null;
  RaisedAmount?: number | null;
  DisplayIndicatorAchievement: boolean | null;
  ShowPercentageAchievementIndicator: boolean | null;
  EndowmentCertificate: boolean | null;
  AllowEnterText?: boolean | null;
  EnterTextLabel?: string | null;
  EnterTextPlaceholder?: string | null;
  IsBriefProject: boolean | null;
  RelatedGiftProjectId: number | null;
  QuantityStock: number | null;
  NameAdmin?: string | null;
  EndowmentCertificateImageId?: string | null;
  /** Association's stored custom QR URL; when absent the UI falls back to the canonical project URL. */
  QrCodeUrl?: string | null;
  /** Pre-loaded shares from the homepage bundle. */
  shares?: ProjectShare[];
  IsProjectCompleted?: boolean;
  CanDonate?: boolean;
}

export interface ApiCategoryResponse {
  Id: number;
  Name: string;
  Description?: string | null;
  ImageId: number | null;
  ImageUrl?: string | null;
  BannerImageId?: number | null;
  BannerImageUrl?: string | null;
  ImageCampaignId?: number | null;
  ImageCampaignUrl?: string | null;
  OrderNumber: number;
  IsActive: boolean;
  AssociationId: number | null;
  ViewHomePage: boolean | null;
  ViewQuickDonation?: boolean | null;
  ViewInCampaigns?: boolean | null;
  ViewInDeductions?: boolean | null;
  HideCategoryProject?: boolean | null;
  ExternalLink?: string | null;
  PaidAmount: number | null;
  NoDoners: number | null;
}

export interface ApiNewsArticleResponse {
  Id: number;
  Title: string;
  Content?: string;
  Description?: string | null;
  ShortDescription?: string | null;
  NewsTypeId: number | null;
  AssociationId: number;
  ImageId?: number | null;
  MainImageId?: number | null;
  ImageUrl?: string | null;
  MainImageUrl?: string | null;
  PublishDate?: Date | null;
  Views?: number | null;
  IsActive: boolean;
  CreateDate?: string | Date;
  LastUpdate?: string | Date;
  SeoId?: number | null;
  PageUrlKey?: string | null;
  CreationDate?: Date;
}

// Authentication Types
export interface User {
  Id: string;
  UserName: string;
  Email?: string | null;
  PhoneNumber?: string | null;
  Name?: string | null;
  FullName?: string | null;
  LockoutEnabled?: boolean;
  [key: string]: unknown;
}

export interface LoginRequest {
  username: string; // Mobile number or username
  password: string; // Password for login
}

export interface RegisterRequest {
  mobile: string; // Mobile number (required)
  password: string; // Password (required)
  name?: string; // Name (optional)
  email?: string; // Email (optional)
  associationId?: number; // Association context (optional)
}

export interface ForgotPasswordRequest {
  mobile: string; // Mobile number
}

export interface ResetPasswordRequest {
  code: string; // Reset code
  mobile: string; // Mobile number
  password: string; // New password
}

/** Nested tokens from POST /auth/login (and similar) — matches backend JSON */
export interface AuthLoginTokensPayload {
  accessToken: string;
  refreshToken?: string;
  /** e.g. `"1h"` or epoch seconds */
  expiresIn?: string | number;
}

/** Raw `data` when backend nests JWTs under `tokens` */
export interface AuthLoginApiData {
  user?: User;
  tokens?: AuthLoginTokensPayload;
  /** Some environments flatten these on `data` */
  accessToken?: string;
  refreshToken?: string;
  expiresIn?: number | string;
}

/** Distinguishes full credentials login from limited checkout-only tokens (`/auth/login-faster`). */
export type AuthSessionKind = "full" | "fast";

/** Normalized client shape after login/register */
export interface AuthResponse {
  accessToken: string;
  refreshToken?: string;
  user?: User;
  expiresIn?: number;
  /** Present when the backend issued a limited session (e.g. login-faster). */
  authSessionKind?: AuthSessionKind;
}

// ==========================================
// Donation System Enums
// ==========================================

export enum EnumDonationStatus {
  NotComplete = 0,
  Pending = 1,
  Success = 2,
  Rejected = 3,
  Cancel = 4,
  Refund = 5,
}

export enum EnumPaymentMethods {
  BankTransfer = 1,
  Visa = 2,
}

export enum EnumPaymentGateway {
  None = 0,
  Moyasar = 1,
  HyperPay = 2,
  StripePay = 3,
  MyFatoorah = 4,
  AlRajhi = 5,
  Urway = 6,
}

export enum EnumGiftSendingType {
  SendNow = 1,
  SendLater = 2,
}

export enum BillDeductionPeriodType {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
  Quarterly = 4,
  Midterm = 5,
  Annual = 6,
}

export enum EnumDonateType {
  Categories = 1,
  Sharing = 2,
  Single = 3,
  Open = 4,
}

// ==========================================
// Cart Request/Response Types
// ==========================================

/** Accepts both PascalCase (API/cart) and camelCase */
export interface AddToCartRequest {
  projectId?: number;
  amount?: number;
  quantity?: number;
  projectShareId?: number;
  ProjectId?: number;
  Amount?: number;
  Quantity?: number;
  ProjectShareId?: number | null;
  DonateTypeId?: number | null;
  CategoryId?: number | null;
  DonationOpen?: boolean;
  IsProduct?: boolean;
  GiftProject?: boolean;
  ProjectName?: string;
  ProjectImage?: string | null;
  categoryId?: number;
  /** Resolved category label kept on guest lines for cart display. */
  categoryName?: string;
  donateTypeId?: number;
  // Gift options
  giftProject?: boolean;
  giftInfo?: GiftInfo;
  sendToName?: string;
  sendToMobile?: string;
  senderName?: string;
  typeSending?: number;
  wantCopyForYou?: boolean;
  messageDateTime?: string;
  // Certificate options
  certificateInfo?: { certificateName?: string; sendCertificateSms?: boolean };
  certificateName?: string;
  isSendCertificateSms?: boolean;
  /** Admin-configured free-text field captured during donation. */
  enterText?: string;
  // Product options
  isProduct?: boolean;
  fromWhatsapp?: boolean;
  [key: string]: unknown;
}

export interface AddRecurringRequest {
  projectId: number;
  amount: number;
  dayNumber: number; // Day of month (1-28)
  periodType: BillDeductionPeriodType;
  projectShareId?: number;
  endDate?: string;
}

export interface CartItemResponse {
  id: number;
  projectId: number;
  projectName: string;
  projectImage?: string;
  quantity: number;
  amount: number;
  totalAmount: number;
  isProduct: boolean;
  shareId?: number;
  shareName?: string;
  sharePrice?: number;
  giftProject?: boolean;
  sendToName?: string;
  sendToMobile?: string;
  senderName?: string;
  certificateName?: string;
  isSendCertificateSms?: boolean;
  enterText?: string;
  billDeductionId?: number;
}

export interface CartTotalResponse {
  items: CartItemResponse[];
  subtotal: number;
  shipping: number;
  total: number;
  itemCount: number;
  hasProducts: boolean;
}

export interface ValidateStockResponse {
  valid: boolean;
  available: number;
}

// ==========================================
// Payment Request/Response Types
// ==========================================

// Cart item for process-cart request
export interface ProcessCartItem {
  /** Absent for category-targeted items such as a general deduction. */
  projectId?: number;
  amount: number;
  quantity?: number;
  shareId?: number;
  categoryId?: number;
  isProduct?: boolean;
  isGift?: boolean;
  giftInfo?: GiftInfo;
  certificateInfo?: CertificateInfo;
  enterText?: string;
  isRecurring?: boolean;
  recurringDate?: string;
  periodType?: number;
  dayNumber?: number;
  endDate?: string;
  totalPeriod?: number;
  openEnded?: boolean;
  donateTypeId?: number;
}

export interface ProcessCartRequest {
  // New API format
  items?: ProcessCartItem[];
  paymentMethod?: "card" | "bank" | "applepay";
  /**
   * "standalone" pays the items in this request on their own and leaves the
   * donor's cart untouched. Absent means the cart is what is being paid.
   */
  checkoutSource?: "cart" | "standalone";
  associationId: number;
  // Affiliate tracking
  affiliateKey?: string;
  visitorId?: string;
  // Guest checkout donor info (when no auth token)
  donorName?: string;
  donorMobile?: string;
  donorEmail?: string;
  // Privacy
  encryptDonorInfo?: boolean;
  shippingAddress?: {
    customerName: string;
    mobile: string;
    email?: string;
    cityId: number;
    address: string;
    details?: string;
  };
  // Legacy fields (for backward compatibility)
  paymentMethodId?: EnumPaymentMethods;
  paymentGateway?: EnumPaymentGateway;
  // For bank transfer
  bankId?: number;
  accountId?: number;
  imageId?: number;
  donorBankText?: string;
  donorBankAccountNum?: string;
  // For card payment
  cardType?: "visa" | "mastercard" | "mada" | "applepay";
  applePay?: boolean;
  encryptAllDataEnable?: boolean;
}

export interface ProcessCartResponse {
  operationCode: string;
  donationId?: number;
  amount: number;
  currency?: string;
  message?: string;
  // Legacy fields (for backward compatibility)
  encryptedId?: string;
  totalAmount?: number;
  statusId?: EnumDonationStatus;
  paymentRequired?: boolean;
  nextStep?: "PAYMENT" | "PENDING_REVIEW" | "COMPLETE";
}

export interface PayOneProjectRequest {
  projectId?: number;
  amount: number;
  quantity?: number;
  associationId: number;
  projectShareId?: number;
  cardType?: string;
  donorName?: string;
  donorMobile?: string;
  donorEmail?: string;
  isRecurring?: boolean;
  recurringDate?: string;
  donateTypeId?: number;
  categoryId?: number;
  giftInfo?: GiftInfo;
  certificateInfo?: CertificateInfo;
  enterText?: string;
  /** Marketing attribution tokens (fall back to jt_* cookies server-side). */
  marketingVisitorId?: string;
  marketingLinkToken?: string;
  marketingClickToken?: string;
}

export interface PaymentStatusResponse {
  donationId?: number;
  statusId: EnumDonationStatus;
  statusName: string;
  totalAmount?: number;
}

export interface PaymentVerificationResponse {
  success: boolean;
  status: string;
  statusId: EnumDonationStatus;
  donation?: DonationDetails;
}

export interface DonationRatingRequest {
  operationCode: string;
  rating: number;
  comment?: string;
}

export interface DonationRatingResponse {
  id: number;
  operationCode: string;
  associationId: number;
  rating: number;
  comment?: string | null;
}

export interface DonationDetails {
  operationCode: string;
  totalAmount: number;
  statusId: EnumDonationStatus;
  paymentMethodId: EnumPaymentMethods;
  donationDate: string;
  items: CartItemResponse[];
  giftVoucherUrl?: string;
  certificateUrl?: string;
}

// ==========================================
// Payment Gateway Types
// ==========================================

export interface PaymentGatewayConfig {
  gateway: string;
  gatewayId: EnumPaymentGateway;
  gatewayNameAr: string;
  isOnlinePaymentActive: boolean;
  credentials: {
    publishableKey?: string;
    entityId?: string;
    madaEntityId?: string;
  };
  features: {
    applePayEnabled: boolean;
    applePayNewEnabled: boolean;
  };
  currency: string;
}

export interface MoyasarInitiateRequest {
  operationCode: string;
  associationId: number;
}

export interface MoyasarInitiateResponse {
  publishableKey: string;
  amount: number;
  currency: string;
  description: string;
  callbackUrl: string;
  metadata: {
    opCode: string;
    associationId: string;
    mobile?: string;
  };
}

export interface MoyasarUpdateRequest {
  operationCode: string;
  moyasarId: string;
  cardType: string;
  associationId: number;
}

export interface MyFatoorahInitiateSessionRequest {
  operationCode: string;
  associationId: number;
}

export interface MyFatoorahInitiateSessionResponse {
  sessionId: string;
  countryCode: string;
  amount: number;
}

export interface MyFatoorahExecuteRequest {
  operationCode: string;
  sessionId: string;
  cardBrand: string;
  associationId: number;
}

export interface MyFatoorahExecuteResponse {
  success: boolean;
  paymentUrl: string;
  invoiceId: string;
}

export interface HyperPayCheckoutRequest {
  operationCode: string;
  associationId: number;
  payType: 1 | 2; // 1=Card, 2=MADA
}

export interface HyperPayCheckoutResponse {
  checkoutId: string;
  entityId: string;
}

export interface StripeCreateSessionRequest {
  operationCode: string;
  associationId: number;
}

export interface StripeCreateSessionResponse {
  sessionId: string;
  publishableKey: string;
  url: string;
}

// ==========================================
// Bank Transfer Types
// ==========================================

export interface BankWithLogo {
  id: number;
  name: string;
  nameAr: string;
  logo?: string;
}

export interface BankAccountDetails {
  id: number;
  bankId: number;
  bankName: string;
  accountNumber: string;
  iban: string;
  accountName?: string;
}

export interface BankWithAccounts {
  bank: BankWithLogo;
  accounts: BankAccountDetails[];
}

export interface BankTransferFormData {
  bankId: number | null;
  accountId: number | null;
  imageId: number | null;
  donorBankText?: string;
  donorBankAccountNum?: string;
}

export interface ReceiptUploadResponse {
  imageId: number;
  filename: string;
  url: string;
}

// ==========================================
// Gift Types (Extended)
// ==========================================

export interface GiftVoucher {
  id: number;
  projectName: string;
  projectImage?: string;
  amount: number;
  senderName?: string;
  sendToName?: string;
  associationName: string;
  associationLogo?: string;
  message?: string;
  donationDate?: string;
  cardImageUrl?: string;
  shareId?: number;
}

export interface SentGift {
  id: number;
  projectName: string;
  amount: number;
  sendToName: string;
  sendToMobile: string;
  senderName: string;
  sentAt?: string;
  status: string;
}

// ==========================================
// Certificate Types
// ==========================================

export interface Certificate {
  id: number;
  certificateName: string;
  projectName: string;
  amount: number;
  donationDate: string;
  certificateImage?: string;
  associationName: string;
  associationLogo?: string;
  operationCode: string;
}

export interface CertificateProject {
  id: number;
  name: string;
  certificateImageId?: string;
  certificateImageUrl?: string;
}

// ==========================================
// Billing/Recurring Types
// ==========================================

export interface DeductionSubscription {
  id: number;
  groupId: number;
  projectId: number;
  projectName: string;
  shareName?: string;
  amount: number;
  periodType: BillDeductionPeriodType;
  totalPeriod: number;
  paidBillsCount: number;
  pendingBillsCount: number;
  nextDueDate?: string;
  startBillsDate: string;
  endBillsDate?: string | null;
  isStopped: boolean;
}

export interface BillDeduction {
  id: number;
  projectId: number;
  projectName: string;
  amount: number;
  dayNumber: number;
  periodType: BillDeductionPeriodType;
  periodTypeName: string;
  nextBillDate: string;
  status: string;
  groupId: number;
}

export interface BillGroup {
  groupId: number;
  bills: BillDeduction[];
  totalAmount: number;
  isActive: boolean;
}

// ==========================================
// Shipping Types
// ==========================================

export interface ShippingCity {
  id: number;
  name: string;
  nameAr?: string;
  shippingCost?: number;
}

export interface ShippingAddress {
  customerName: string;
  mobile: string;
  email?: string;
  cityId: number;
  cityName?: string;
  address: string;
  details?: string;
}

// ==========================================
// Error Types
// ==========================================

export interface ApiErrorResponse {
  success: false;
  error: string;
  message: string;
  code?: string;
}

export type DonationErrorCode =
  | "CART_EMPTY"
  | "INVALID_PAYMENT_METHOD"
  | "BANK_INFO_REQUIRED"
  | "DONATION_NOT_FOUND"
  | "PAYMENT_FAILED"
  | "INVALID_SIGNATURE"
  | "DUPLICATE_OPERATION"
  | "INSUFFICIENT_STOCK"
  | "PROJECT_NOT_FOUND"
  | "CERTIFICATE_NOT_SUPPORTED";
